import React, { useRef, useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AllNotifications } from "./notification-tabs/all";
import { Leads } from "./notification-tabs/leads";
import { Tasks } from "./notification-tabs/tasks";
import { Pipeline } from "./notification-tabs/pipeline";
import { InboxNotification } from "./notification-tabs/inbox";
import { useNotificactionStore } from "@/containers/Mail/store/notitication-store";
import { useMarkRead } from "@/containers/Mail/hooks/use-mark-read";

const NotificationDialog = ({ children }: React.PropsWithChildren) => {
  const closeRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const { unreads, clearUnreads } = useNotificactionStore();
  const { markAllAsRead, isMarkingAllRead } = useMarkRead();

  // On open, land on the tab that actually has content. The badge is driven
  // by unread outreach replies (the mail store), which live under Inbox — so
  // when there are unreads, open there instead of the (often empty) All tab.
  // Otherwise the bell shows a count but opens to a blank list.
  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) setActiveTab(unreads > 0 ? "inbox" : "all");
  };

  const handleMarkAllAsRead = () => {
    if (activeTab === "inbox" && unreads > 0) {
      markAllAsRead(undefined, { onSuccess: clearUnreads });
    }
  };

  const hasUnreadNotifications = () => {
    if (activeTab === "inbox") return unreads > 0;
    return false;
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent showCloseButton={false} className="p-0 max-w-md">
        <DialogHeader className="p-2 pb-0 relative">
          <div className="flex items-center gap-3">
            <div>
              <h3 className="font-medium text-lg">Notifications</h3>
            </div>
            <button
              onClick={handleMarkAllAsRead}
              disabled={!hasUnreadNotifications() || isMarkingAllRead}
              className="text-primary disabled:text-primary/50 absolute right-5 text-sm"
            >
              Mark all as read
            </button>
          </div>
        </DialogHeader>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="h-auto gap-2 justify-start rounded-none border-b bg-transparent relative px-4 py-1 text-foreground w-full">
            <div className="bg-border h-px w-full absolute bottom-0 left-0" />
            <TabsTrigger
              value="all"
              className="after:-mb-1 relative after:absolute max-w-max bg-none border-none after:inset-x-0 after:bottom-0 after:h-0.5 hover:bg-accent hover:text-foreground  data-[state=active]:shadow-none data-[state=active]:hover:bg-accent data-[state=active]:after:bg-primary"
            >
              All
            </TabsTrigger>
            <TabsTrigger
              value="leads"
              className="after:-mb-1 relative after:absolute max-w-max bg-none border-none after:inset-x-0 after:bottom-0 after:h-0.5 hover:bg-accent hover:text-foreground  data-[state=active]:shadow-none data-[state=active]:hover:bg-accent data-[state=active]:after:bg-primary"
            >
              Leads
            </TabsTrigger>
            <TabsTrigger
              value="tasks"
              className="after:-mb-1 relative after:absolute max-w-max bg-none border-none after:inset-x-0 after:bottom-0 after:h-0.5 hover:bg-accent hover:text-foreground  data-[state=active]:shadow-none data-[state=active]:hover:bg-accent data-[state=active]:after:bg-primary"
            >
              Tasks
            </TabsTrigger>
            <TabsTrigger
              value="pipelines"
              className="after:-mb-1 relative after:absolute max-w-max bg-none border-none after:inset-x-0 after:bottom-0 after:h-0.5 hover:bg-accent hover:text-foreground  data-[state=active]:shadow-none data-[state=active]:hover:bg-accent data-[state=active]:after:bg-primary"
            >
              Pipelines
            </TabsTrigger>
            <TabsTrigger
              value="inbox"
              className="after:-mb-1 relative after:absolute max-w-max bg-none border-none after:inset-x-0 after:bottom-0 after:h-0.5 hover:bg-accent hover:text-foreground  data-[state=active]:shadow-none data-[state=active]:hover:bg-accent data-[state=active]:after:bg-primary"
            >
              Inbox
              {unreads > 0 && <span className="text-xs text-primary rounded-full bg-primary/10 px-1.5 py-0.5">{unreads}</span>}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="all">
            <AllNotifications />
          </TabsContent>
          <TabsContent value="leads">
            <Leads />
          </TabsContent>
          <TabsContent value="tasks">
            <Tasks />
          </TabsContent>
          <TabsContent value="pipelines">
            <Pipeline />
          </TabsContent>
          <TabsContent value="inbox">
            <InboxNotification />
          </TabsContent>
        </Tabs>
      </DialogContent>
      <DialogClose ref={closeRef} className="hidden" />
    </Dialog>
  );
};

export { NotificationDialog };
